/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * HtmlTooltip
 *
 * @file HtmlTooltip.jsx
 * @author Mohamed yasar arabath M
 */

import React from 'react';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import mixins from '../../assets/styles/mixins.scss';
import { getStatusStyles } from '../../common/statusColorUtils';
import ConditionalWrapper from './ConditionalWrapper';

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} arrow />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'transparent',
    maxWidth: 220,
    padding: 0,
    boxShadow: '0px 2px 1px -1px rgba(13, 0, 77, 0.15), 0px 1px 1px 0px rgba(13, 0, 77, 0.10), 0px 1px 3px 0px rgba(13, 0, 77, 0.05)'
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: mixins.whiteFont,
    fontSize: 14,
    '&::before': {
      border: '1px solid rgba(13, 0, 77, 0.15)'
    }
  },
  [`& .${tooltipClasses.tooltipPlacementBottom} .${tooltipClasses.arrow}`]: {
    top: '1px !important'
  }
});

const LabelWrapper = styled('div')({
  '&&': {
    background: mixins.whiteFont,
    borderRadius: '4px',
    border: '1px solid rgba(13, 0, 77, 0.15)',
    padding: '2px 8px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  }
});

const TextWrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  }
});

const TooltipText = styled('div', {
  shouldForwardProp: (prop) => prop !== 'showBullet'
})(({ showBullet }) => ({
  '&&': {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize12,
    lineHeight: mixins.lineHeight20,
    display: 'flex',
    flexDirection: 'column',
    gap: showBullet ? '12px' : '5px'
  }
}));

const SegmentRow = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
    width: 'fit-content'
  }
});

const StatusDotContainer = styled('div')({
  '&&': {
    width: 8,
    height: 8,
    position: 'relative'
  }
});

const DotBase = styled('div')({
  '&&': {
    borderRadius: '50%',
    width: 8,
    height: 8,
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    top: 0,
    bottom: 0
  }
});

const TooltipLabel = styled('span')({
  '&&': {
    fontWeight: mixins.fontWeight400
  }
});

const TooltipValue = styled('span')({
  '&&': {
    fontWeight: mixins.fontWeight600,
    marginLeft: 4
  }
});

const StatusTemplate = ({ segments, isPercentage, showBullet }) => (
  <LabelWrapper>
    <TextWrapper>
      <TooltipText showBullet={showBullet}>
        {segments.map((segment) => {
          const dotColor = segment?.dotColor || getStatusStyles(segment?.type, { showBullet })?.color;
          const value = isPercentage ? `${segment.value}%` : segment.value;

          return (
            <ConditionalWrapper
              key={segment.label}
              condition={showBullet}
              wrapper={SegmentRow}
            >
              <>
                {showBullet && (
                <StatusDotContainer>
                  <DotBase
                    style={{ backgroundColor: dotColor, color: dotColor }}
                  />
                </StatusDotContainer>
                )}
                <span style={{ color: segment.color || getStatusStyles(segment?.id)?.color }}>
                  <TooltipLabel>{segment.label}:</TooltipLabel><TooltipValue>{value}</TooltipValue>
                </span>
              </>
            </ConditionalWrapper>
          );
        })}
      </TooltipText>
    </TextWrapper>
  </LabelWrapper>
);

StatusTemplate.defaultProps = {
  isPercentage: true,
  showBullet: false
};

StatusTemplate.propTypes = {
  segments: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired
    })
  ).isRequired,
  isPercentage: PropTypes.bool,
  showBullet: PropTypes.bool
};

export default HtmlTooltip;
export { StatusTemplate };
